import { type ReactElement } from 'react'
import { isNotEmpty } from '@terros/common'
import { Link, useNavigate, createFileRoute } from '@tanstack/react-router'
import { useToastError } from '@hooks'
import { useAddGame, useGames, useProfile, useTranslation } from '@front/model'
import { ButtonAdd, NotReady, Page, PermissionTooltip, TButton } from '@components'
import { NoCompetitions } from './NoCompetitions'
import { CompetitionsList } from './CompetitionList'

function Competitions(): ReactElement {
  const { t } = useTranslation()
  const { user } = useProfile()
  const { data, isLoading, error, refetch, isFetching } = useGames()
  const { isAdding, error: addError } = useAddGame()
  useToastError(addError)
  const navigate = useNavigate()

  if (isLoading) return <NotReady id='competitions' isLoading={isLoading} />
  if (error || !data) return <NotReady data={data} error={error} id='competitions' />

  const onAddNavigate = async (): Promise<void> => {
    navigate({ to: '/competition/add' })
  }

  const gamesExist = isNotEmpty(data)

  return (
    <Page>
      <Page.Heading isFetching={isFetching} refetch={refetch} title={t('Competitions')}>
        <PermissionTooltip permissionType='game:save' targetId={user?.userId}>
          <ButtonAdd loading={isAdding} onClick={onAddNavigate}>
            {t('Add Competition')}
          </ButtonAdd>
        </PermissionTooltip>
        <TButton component={Link} icon='archive' to='trash' tooltip={t('View Trash')} variant='secondary' />
      </Page.Heading>
      <Page.Content scrollArea>
        {gamesExist ? (
          <CompetitionsList games={data} onAdd={onAddNavigate} />
        ) : (
          <NoCompetitions onAdd={onAddNavigate} />
        )}
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/competition/')({
  component: Competitions,
})
